import React from 'react'
// libs
import { change } from 'redux-form'
import { useDispatch } from 'react-redux'
// components
import { ColorSelect } from 'components/FormElements'
import InputLabel from 'components/FormElements/components/InputLabel'
// icons
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
// constants
import { CHART_PALETTES } from 'constants/charts'
// schemas
import { ChartPalette } from 'schemas/chartConfig'
// types
import { WrappedFieldArrayProps } from 'redux-form/lib/FieldArray'
// views
import { AddColor, ColorCode, ColorItem, Colors, RemoveButton, Swatch } from './views'

const DEFAULT_PALETTE = CHART_PALETTES[ChartPalette.Default]

interface Props {
  label: string
  // The schema's limits: how many colors, and what a color may be (hex)
  maxItems?: number
  pattern?: string
}

// The colors of the CUSTOM palette, used in turn: a swatch per color, a new one starts from the default palette
const CustomColors: React.FC<WrappedFieldArrayProps<string> & Props> = ({ fields, meta, label, maxItems, pattern }) => {
  const dispatch = useDispatch()

  const colorPattern = React.useMemo(() => pattern && new RegExp(pattern), [pattern])

  const canAdd = maxItems === undefined || fields.length < maxItems

  const addColor = () => {
    fields.push(DEFAULT_PALETTE[fields.length % DEFAULT_PALETTE.length])
  }

  // The picker gives "transparent" for a color with no opacity: the schema takes hex only, so it is not kept
  const setColor = (field: string) => (color: string) => {
    if (!colorPattern || colorPattern.test(color)) {
      dispatch(change(meta.form, field, color))
    }
  }

  return (
    <div>
      <InputLabel shrink>{label}</InputLabel>
      {!!fields.length && (
        <Colors>
          {fields.map((field, index) => {
            const color = fields.get(index)

            return (
              <ColorItem key={field}>
                {/* The picker keeps its own color: after a removal the colors shift, so it starts again */}
                <ColorSelect
                  key={fields.length}
                  initialColor={color}
                  trigger={<Swatch color={color} />}
                  onChange={setColor(field)}
                />
                <RemoveButton title='Remove color' onClick={() => fields.remove(index)}>
                  <CloseRoundedIcon />
                </RemoveButton>
                <ColorCode>{color}</ColorCode>
              </ColorItem>
            )
          })}
        </Colors>
      )}
      {canAdd && <AddColor onClick={addColor}>+ add color</AddColor>}
    </div>
  )
}

export default CustomColors
